import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useDemo } from '@/contexts/DemoContext';
import { 
  Search, Mic, Bell, ChevronRight, QrCode, AlertCircle
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { toast } from 'sonner';
import API from '@/utils/api';
import APP_ICONS, { PastelIcon } from '@/utils/appIcons';
import { haptics } from '@/utils/haptics';
import QRCode from 'qrcode';

/**
 * HomePage - Clean iOS-style Dashboard
 * 
 * Features:
 * - Dynamic time-based greeting
 * - Centered "+" button in bottom nav (handled by BottomNav component)
 * - Featured Emergency Binder card with refined QR button
 * - Larger 3D pastel icons with transparent backgrounds
 * - Improved text contrast for accessibility
 * - Calm, uncluttered design
 */

// Get dynamic greeting based on time of day (no emoji)
const getDynamicGreeting = () => {
  const hour = new Date().getHours();
  
  if (hour >= 5 && hour < 12) {
    return { text: 'Good morning' };
  } else if (hour >= 12 && hour < 17) {
    return { text: 'Good afternoon' };
  } else if (hour >= 17 && hour < 22) {
    return { text: 'Good evening' };
  } else {
    return { text: 'Good night' };
  }
};

// Feature card configuration with 3D pastel icons
const FEATURE_CARDS = [
  {
    id: 'medical',
    title: 'Medical & Doctor Visits',
    icon: APP_ICONS.medicalInfo,
    path: '/medical',
    gradient: 'linear-gradient(135deg, #D1FAE5 0%, #ECFDF5 100%)',
  },
  {
    id: 'documents',
    title: 'Documents',
    icon: APP_ICONS.documents,
    path: '/documents',
    gradient: 'linear-gradient(135deg, #D7F3FF 0%, #E5D9FF 100%)',
  },
  {
    id: 'parking',
    title: 'Parking Saver',
    icon: APP_ICONS.parking,
    path: '/parking',
    gradient: 'linear-gradient(135deg, #DAEDFF 0%, #FFFFFF 100%)',
  },
  {
    id: 'dates',
    title: 'Important Dates',
    icon: APP_ICONS.dates,
    path: '/dates',
    gradient: 'linear-gradient(135deg, #FFE5EC 0%, #FFDBE3 100%)',
  },
  {
    id: 'car-maintenance',
    title: 'Car Maintenance',
    icon: APP_ICONS.carMaintenance,
    path: '/car-maintenance',
    gradient: 'linear-gradient(135deg, #DAFFED 0%, #FFFFFF 100%)',
  },
  {
    id: 'contacts',
    title: 'Network',
    icon: APP_ICONS.familyContacts,
    path: '/contacts-places',
    gradient: 'linear-gradient(135deg, #E3D7FF 0%, #FFEAD7 100%)',
  },
];

const HomePage = () => {
  const { user } = useAuth();
  const { isDemoMode, getDemoUser, getBinderCompletion, getProfiles } = useDemo();
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [binderCompletion, setBinderCompletion] = useState(0);
  const [binderDetails, setBinderDetails] = useState({ filled_sections: 0, total_sections: 7 });
  const [greeting, setGreeting] = useState(getDynamicGreeting());
  const [tappedCard, setTappedCard] = useState(null);
  const [primaryProfileId, setPrimaryProfileId] = useState(null);
  
  // QR Code state
  const [showQR, setShowQR] = useState(false);
  const [qrCodeUrl, setQrCodeUrl] = useState('');
  const [accessToken, setAccessToken] = useState(null);
  const [generatingQR, setGeneratingQR] = useState(false);

  // Get current user (real or demo)
  const currentUser = isDemoMode ? getDemoUser() : user;

  // Update greeting periodically
  useEffect(() => {
    const interval = setInterval(() => {
      setGreeting(getDynamicGreeting());
    }, 60000); // Update every minute
    return () => clearInterval(interval);
  }, []);

  // Fetch dashboard stats
  useEffect(() => {
    fetchDashboardStats();
  }, [isDemoMode]);

  const fetchDashboardStats = async () => {
    try {
      // DEMO MODE: Use local demo data
      if (isDemoMode) {
        const demoCompletion = getBinderCompletion();
        setBinderCompletion(demoCompletion.completion_percentage);
        setBinderDetails({
          filled_sections: demoCompletion.filled_sections,
          total_sections: demoCompletion.total_sections
        });
        
        // Get primary profile from demo data
        const demoProfiles = getProfiles();
        if (demoProfiles?.length > 0) {
          setPrimaryProfileId(demoProfiles[0].id);
        }
        return;
      }
      
      // REAL MODE: Fetch from API
      const binderRes = await API.get('/emergency-binder/completion').catch(() => ({ 
        data: { completion_percentage: 0, filled_sections: 0, total_sections: 7 } 
      }));
      setBinderCompletion(binderRes.data?.completion_percentage || 0);
      setBinderDetails({
        filled_sections: binderRes.data?.filled_sections || 0,
        total_sections: binderRes.data?.total_sections || 7
      });
      
      // Get primary profile for QR code
      const profilesRes = await API.get('/emergency-binder/profiles').catch(() => ({ data: [] }));
      if (profilesRes.data?.length > 0) {
        const primaryProfile = profilesRes.data[0];
        setPrimaryProfileId(primaryProfile.id);
        
        // Check if QR token exists
        const tokenRes = await API.get(`/emergency-binder/profiles/${primaryProfile.id}/access-token`).catch(() => ({ data: {} }));
        if (tokenRes.data?.access_token) {
          setAccessToken(tokenRes.data.access_token);
        }
      }
    } catch (error) {
      console.error('Failed to fetch dashboard stats:', error);
    }
  };

  // Handle QR code button click
  const handleQuickQR = async (e) => {
    e.stopPropagation();
    haptics.tap();
    
    // DEMO MODE: Show info toast
    if (isDemoMode) {
      toast.info('QR Code Demo', {
        description: 'In the real app, this generates a shareable emergency QR code for first responders.'
      });
      return;
    }
    
    if (!primaryProfileId) {
      toast.error('Create an emergency binder first');
      navigate('/emergency-binder');
      return;
    }
    
    // If we already have a QR code, show it
    if (accessToken && qrCodeUrl) {
      setShowQR(true);
      return;
    }
    
    // Generate QR code
    try {
      setGeneratingQR(true);
      
      // Get or create access token
      let token = accessToken;
      if (!token) {
        const response = await API.post(`/emergency-binder/profiles/${primaryProfileId}/generate-access-token`);
        token = response.data.access_token;
        setAccessToken(token);
      }
      
      // Generate QR code image
      const shareUrl = `${window.location.origin}/emergency-view/${token}`;
      const qrUrl = await QRCode.toDataURL(shareUrl, {
        width: 300,
        margin: 2,
        color: { dark: '#000000', light: '#ffffff' }
      });
      setQrCodeUrl(qrUrl);
      setShowQR(true);
    } catch (error) {
      console.error('Failed to generate QR:', error);
      toast.error('Failed to generate QR code');
    } finally {
      setGeneratingQR(false);
    }
  };

  const handleCopyEmergencyLink = async () => {
    if (!accessToken) return;
    const shareUrl = `${window.location.origin}/emergency-view/${accessToken}`;
    await navigator.clipboard.writeText(shareUrl);
    toast.success('Emergency link copied!');
  };

  // Handle search
  const handleSearch = useCallback((e) => {
    e?.preventDefault();
    if (searchQuery.trim()) {
      haptics.tap();
      navigate(`/search?q=${encodeURIComponent(searchQuery)}`);
    }
  }, [searchQuery, navigate]);

  // Handle card click with animation
  const handleCardClick = (card, isEmergencyBinder = false) => {
    haptics.tap();
    setTappedCard(card.id || 'emergency-binder');
    
    // Delay navigation for animation
    setTimeout(() => {
      setTappedCard(null);
      navigate(isEmergencyBinder ? '/emergency-binder' : card.path);
    }, 150);
  };

  // Get motivational helper message for emergency binder
  const getBinderHelperMessage = () => {
    if (binderCompletion === 0) {
      return 'Start setting up your emergency information';
    } else if (binderCompletion <= 20) {
      return 'Great start! Keep adding your medical details';
    } else if (binderCompletion <= 60) {
      return "You're making progress. Add more medical details";
    } else if (binderCompletion < 100) {
      return 'Almost finished! Complete your emergency binder';
    } else {
      return 'Your emergency binder is ready ✓';
    }
  };

  // Get progress label text
  const getProgressLabel = () => {
    const { filled_sections, total_sections } = binderDetails;
    if (binderCompletion === 0) {
      return 'Not started';
    } else if (binderCompletion === 100) {
      return 'Complete';
    } else {
      return `${filled_sections} of ${total_sections} sections`;
    }
  };

  return (
    <div className="pastel-gradient min-h-screen" data-testid="home-page">
      {/* Header */}
      <header className="safe-area-top px-4 pt-4 pb-2">
        {/* Top Row: Greeting + Notification */}
        <div className="flex items-center justify-between mb-3">
          {/* Dynamic Greeting */}
          <div className="flex-1 pr-4">
            <h2 className="text-xl font-medium text-slate-700">
              {greeting.text}
            </h2>
            {currentUser?.name && (
              <p className="text-sm font-medium text-slate-600 mt-0.5">
                {currentUser.name.split(' ')[0]}
              </p>
            )}
          </div>
          
          {/* Notifications Button - 44x44 touch target */}
          <button 
            className="w-11 h-11 rounded-full bg-white/70 flex items-center justify-center shadow-sm active:scale-95 transition-transform flex-shrink-0"
            data-testid="notifications-btn"
            aria-label="View notifications"
            onClick={() => {
              haptics.tap();
              navigate('/settings/notifications');
            }}
          >
            <Bell className="h-5 w-5 text-slate-600" />
          </button>
        </div>

        {/* Separator line */}
        <div className="h-[2px] bg-slate-200/80 mb-2" />

        {/* Search Bar - improved placeholder contrast */}
        <form onSubmit={handleSearch} className="search-bar flex items-center gap-3 px-4 py-3">
          <Search className="h-5 w-5 text-slate-500 flex-shrink-0" aria-hidden="true" />
          <input
            type="text"
            placeholder="Search your life..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="flex-1 bg-transparent outline-none text-base text-slate-700 placeholder:text-slate-500"
            data-testid="search-input"
            aria-label="Search your life"
          />
          <button 
            type="button" 
            className="w-11 h-11 -mr-2 flex items-center justify-center"
            aria-label="Voice search"
          >
            <Mic className="h-5 w-5 text-slate-500" />
          </button>
        </form>
      </header>

      {/* Feature Cards */}
      <main className="px-4 pt-2 space-y-3">
        {/* Featured Emergency Binder Card - Full Width */}
        {/* Using div with role="button" to avoid nested button issue with QR button */}
        <div
          role="button"
          tabIndex={0}
          onClick={() => handleCardClick({}, true)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              handleCardClick({}, true);
            }
          }}
          className={cn(
            "w-full text-left p-4 rounded-2xl relative overflow-hidden cursor-pointer",
            "transition-all duration-150 ease-out",
            "focus:outline-none focus:ring-2 focus:ring-purple-400 focus:ring-offset-2",
            tappedCard === 'emergency-binder' 
              ? "scale-[1.02] shadow-xl" 
              : "shadow-md hover:shadow-lg"
          )}
          style={{ 
            background: 'linear-gradient(135deg, #F3E8FF 0%, #EDE9FE 50%, #FFFFFF 100%)',
          }}
          data-testid="feature-card-emergency-binder"
          aria-label={`Emergency Binder, ${binderCompletion}% complete. Press Enter to open.`}
        >
          {/* QR Button - absolute positioned in upper-right */}
          {primaryProfileId && (
            <button
              onClick={handleQuickQR}
              className={cn(
                "absolute top-3 right-3 w-9 h-9 rounded-full bg-white/80 shadow-sm flex items-center justify-center",
                "transition-all duration-150 active:scale-90 hover:bg-white z-10",
                "focus:outline-none focus:ring-2 focus:ring-purple-400",
                generatingQR && "animate-pulse"
              )}
              aria-label="Generate emergency QR code"
              title="Generate QR code"
            >
              {generatingQR ? (
                <div className="w-4 h-4 border-2 border-purple-500 border-t-transparent rounded-full animate-spin" />
              ) : (
                <QrCode className="h-4 w-4 text-purple-600" />
              )}
            </button>
          )}
          
          <div className="flex items-center gap-3">
            {/* Icon */}
            <div className="flex-shrink-0">
              <PastelIcon 
                src={APP_ICONS.emergencyBinder} 
                alt=""
                size={72}
              />
            </div>
            
            {/* Content - centered within main area */}
            <div className="flex-1 min-w-0 pr-6">
              {/* Title */}
              <h3 className="font-semibold text-slate-800 text-lg leading-tight mb-2">
                Emergency Binder
              </h3>
              
              {/* Progress Bar Row - aligned with percentage */}
              <div className="flex items-center gap-2 mb-2.5">
                <div className="flex-1 h-2.5 bg-white/60 rounded-full overflow-hidden">
                  <div 
                    className={`h-full rounded-full transition-all duration-500 ${
                      binderCompletion === 100 
                        ? 'bg-gradient-to-r from-emerald-500 to-emerald-400' 
                        : 'bg-gradient-to-r from-purple-500 to-purple-400'
                    }`}
                    style={{ width: `${binderCompletion}%` }}
                    role="progressbar"
                    aria-valuenow={binderCompletion}
                    aria-valuemin={0}
                    aria-valuemax={100}
                  />
                </div>
                <span className={`text-sm font-semibold whitespace-nowrap min-w-[36px] text-right ${
                  binderCompletion === 100 ? 'text-emerald-600' : 'text-purple-600'
                }`}>
                  {binderCompletion}%
                </span>
              </div>
              
              {/* Progress Label */}
              <p className="text-xs font-medium text-slate-600 mb-1">
                {getProgressLabel()}
              </p>
              
              {/* Helper Message - updated text */}
              <p className="text-sm text-slate-600 leading-snug pr-2">
                {binderCompletion === 100 
                  ? 'Your emergency binder is ready ✓'
                  : 'Add more medical details to keep your binder ready.'}
              </p>
            </div>
            
            {/* Arrow - vertically centered with progress info */}
            <ChevronRight className="h-5 w-5 text-slate-400 flex-shrink-0" aria-hidden="true" />
          </div>
        </div>

        {/* Regular Feature Cards Grid */}
        <div className="grid grid-cols-2 gap-3">
          {FEATURE_CARDS.map((card) => (
            <button
              key={card.id}
              onClick={() => handleCardClick(card)}
              className={cn(
                "text-left p-3.5 min-h-[130px] flex flex-col rounded-2xl overflow-hidden",
                "transition-all duration-150 ease-out",
                tappedCard === card.id 
                  ? "scale-[1.03] shadow-xl" 
                  : "shadow-md hover:shadow-lg"
              )}
              style={{ background: card.gradient }}
              data-testid={`feature-card-${card.id}`}
              aria-label={card.title}
            >
              {/* Icon - larger size, less padding around it */}
              <div className="mb-1.5">
                <PastelIcon 
                  src={card.icon} 
                  alt=""
                  size={72}
                />
              </div>
              
              {/* Title - consistent alignment */}
              <h3 className="font-semibold text-slate-800 text-base mt-auto leading-tight">
                {card.title}
              </h3>
            </button>
          ))}
        </div>
      </main>

      {/* Quick Emergency QR Modal */}
      {showQR && qrCodeUrl && (
        <div 
          className="fixed inset-0 z-[70] bg-white flex flex-col items-center justify-center p-6"
          onClick={() => setShowQR(false)}
          data-testid="home-qr-modal"
          role="dialog"
          aria-label="Emergency QR Code"
        >
          <div className="text-center max-w-sm" onClick={(e) => e.stopPropagation()}>
            <div className="bg-red-600 text-white px-5 py-2.5 rounded-xl mb-5 inline-flex items-center gap-2">
              <AlertCircle className="h-5 w-5" aria-hidden="true" />
              <span className="text-lg font-bold">EMERGENCY QR</span>
            </div>
            
            <p className="text-slate-600 mb-5">
              Show this to EMTs or first responders
            </p>
            
            {/* Large QR Code */}
            <div className="bg-white p-5 rounded-2xl shadow-xl inline-block mb-5 border-4 border-slate-200">
              <img src={qrCodeUrl} alt="Emergency QR Code" className="w-56 h-56 sm:w-64 sm:h-64" />
            </div>
            
            <p className="text-xs text-slate-500 mb-5">
              Scan to view medical information without login
            </p>
            
            {/* Action Buttons - 44pt touch targets */}
            <div className="flex gap-3 justify-center">
              <button
                onClick={handleCopyEmergencyLink}
                className="h-12 px-5 bg-slate-100 rounded-xl text-slate-700 font-medium flex items-center gap-2 active:scale-95 transition-transform"
                aria-label="Copy emergency link to clipboard"
              >
                Copy Link
              </button>
              <button
                onClick={() => setShowQR(false)}
                className="h-12 px-5 bg-slate-800 text-white rounded-xl font-medium flex items-center gap-2 active:scale-95 transition-transform"
                aria-label="Close QR code modal"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default HomePage;
